import {Checkbox} from '@wandb/components';
// @todo: Migrate to 'TagGroup' from @wandb/components. For more details see:
// https://wandb.github.io/components/?path=/docs/migration-guide--docs
// eslint-disable-next-line wandb/no-deprecated-imports
import {Tag} from '@wandb/weave/components/Tag';
import {TailwindContents} from '@wandb/weave/components/Tailwind';
import React, {memo, useEffect, useRef} from 'react';

import {useSelectedCountsWithRefetch} from '../../state/graphql/selectedCountQuery';
import type * as CustomRunNamesTypes from '../../state/views/customRunNames/types';
import * as ViewHooks from '../../state/views/hooks';
import type * as RunSetTypes from '../../state/views/runSet/types';
import * as TempSelectionsActions from '../../state/views/tempSelections/actions';
import type * as TempSelectionsTypes from '../../state/views/tempSelections/types';
import {type RunWithSupportedKeys} from '../../util/runs';
import * as SM from '../../util/selectionmanager';
import {RunSelectAll} from '../RunSelectAll';
import {useRunSelectorAnalyticsLocation} from './RunSelectorAnalyticsLocationContext';
import {
  useRunSelectorFeatureDisabled,
  useRunSelectorFeatureEnabled,
} from './RunSelectorFeaturesContext';
import {useRunSetGroupSelectionsRef} from './RunSelectorRunSetContext';

export interface RunsTableHeaderProps {
  totalRowCount?: number;
  displayedRows: RunWithSupportedKeys[];
  runSetRef: RunSetTypes.Ref;
  tempSelectionsRef: TempSelectionsTypes.Ref;
  customRunNamesRef?: CustomRunNamesTypes.Ref;
  showTempSelection: boolean;
  onlyShowSelected?: boolean;
  isSingleMode?: boolean;
  onAllItemsTempSelected(): void;
  onAllItemsTempUnselected(): void;
  onVisibilitySelectAll(): void;
  onVisibilitySelectNone(): void;
  onSetOnlyShowSelected(newSetting: boolean): void;
  onSelectVisibleRuns(): void;
}

const RunsTableHeaderComp = (props: RunsTableHeaderProps) => {
  const analyticsLocation = useRunSelectorAnalyticsLocation();

  const prevRowCountRef = useRef(props.totalRowCount);
  const [selectedCounts, refetchCounts] = useSelectedCountsWithRefetch(
    props.runSetRef,
    props.tempSelectionsRef,
    props.customRunNamesRef
  );
  useEffect(() => {
    // Refetch counts when totalRowCount changes. This is not the most robust
    // check, nor particularly elegant, so should be factored out at some point.
    if (prevRowCountRef.current !== props.totalRowCount) {
      refetchCounts();
      prevRowCountRef.current = props.totalRowCount;
    }
  });
  const groupSelectionsRef = useRunSetGroupSelectionsRef();
  const tempSelectNone = ViewHooks.useViewActionBindAll(
    TempSelectionsActions.selectNone,
    props.tempSelectionsRef,
    props.runSetRef
  );
  const tempSelectSome = ViewHooks.useViewActionBindAll(
    TempSelectionsActions.selectSome,
    props.tempSelectionsRef,
    props.runSetRef,
    props.displayedRows
  );
  const tempSelectAllVisible = ViewHooks.useViewActionBindAll(
    TempSelectionsActions.selectAllVisible,
    props.tempSelectionsRef,
    props.runSetRef
  );

  const groupSelections = ViewHooks.usePart(groupSelectionsRef);
  const tempSelections = ViewHooks.usePart(props.tempSelectionsRef);
  const groupTempSelections = {
    grouping: groupSelections.grouping,
    selections: tempSelections,
    expandedRowAddresses: groupSelections.expandedRowAddresses,
  };
  const rootTempCheckedState = SM.getCheckedState(
    groupTempSelections,
    {} as RunWithSupportedKeys,
    0
  );

  const isEnabled = useRunSelectorFeatureEnabled();
  const isDisabled = useRunSelectorFeatureDisabled();
  const isCheckboxDisabled =
    isDisabled('tag') && isDisabled('move') && isDisabled('delete');

  const selectedCountText =
    selectedCounts.tempSelectedCount > 0
      ? `${selectedCounts.tempSelectedCount.toLocaleString()} selected`
      : `${selectedCounts.selectedCount.toLocaleString()} visualized`;

  return (
    <TailwindContents>
      <div className="inline-flex items-center gap-2 -ml-1">
        {!isCheckboxDisabled && props.showTempSelection && (
          <Checkbox
            aria-label="Select all runs"
            data-test="select-all-runs-checkbox"
            isSelected={rootTempCheckedState === 'checked'}
            isIndeterminate={rootTempCheckedState === 'partial'}
            onPress={() => {
              if (rootTempCheckedState !== 'unchecked') {
                tempSelectNone();
                props.onAllItemsTempUnselected();
              } else {
                tempSelectSome();
                props.onAllItemsTempSelected();
              }
              window.analytics?.track('Select all runs clicked', {
                location: analyticsLocation,
              });
            }}
          />
        )}
        {!props.isSingleMode && isEnabled('toggle-visibility') && (
          <RunSelectAll
            allCheckedState={SM.getCheckedState(
              groupSelections,
              {} as RunWithSupportedKeys,
              0
            )}
            onSelectAll={props.onVisibilitySelectAll}
            onSelectNone={props.onVisibilitySelectNone}
            onlyShowSelected={props.onlyShowSelected}
            selectAllVisible={tempSelectAllVisible}
            setOnlyShowSelected={props.onSetOnlyShowSelected}
            onSelectVisibleRuns={props.onSelectVisibleRuns}
          />
        )}
        <span className="flex items-center gap-6 ml-4">
          Name
          <span data-test="selected-count" className="normal-case">
            <Tag color="moon" label={selectedCountText} />
          </span>
        </span>
      </div>
    </TailwindContents>
  );
};

export const RunsTableHeader = memo(RunsTableHeaderComp);
